import {isFeatureEnabled} from 'feature-flags';
import {
  Action,
  Card,
  getFilePreviewUrl,
  openDeleteDialog,
  openDialog,
  showNotification,
} from 'platform/components';
import {match} from 'ts-pattern';
import {Nullish} from 'utility-types';

import {isNil, mergeAll} from 'ramda';
import {isArray, isTrue} from 'ramda-adjunct';

import {useSubscribeToNotificationsQuery} from '@dms/api/appsync';
import {useGetEsignoServiceQuery} from '@dms/api/core';
import {
  useAgreeConsentMutation,
  useDeleteConsentMutation,
  useRejectConsentMutation,
  useWithdrawConsentMutation,
} from '@dms/api/customer';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {
  DmsDataGrid,
  downloadHostedFile,
  handleApiError,
  refreshDmsDatagrid,
  usePermissions,
} from '@dms/shared';

import {buildArray, openFile} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

import {DOCUMENT_SIGNATURE_DIALOG_ID} from '../../(Sections)/SignDocumentStepper/constants/DOCUMENT_SIGNATURE_DIALOG_ID';
import {SignDocumentStepper} from '../../(Sections)/SignDocumentStepper/SignDocumentStepper';
import {AddConsentForm} from './AddConsentForm';

interface GDPRConsentsProps {
  customerId: string;
  businessCaseId?: string;
  title?: string | Nullish;
  subtitle?: string | Nullish;
}

const PREDEFINE_COLUMNS = ['fileName', 'originalFileName'] as const;
export const GDPR_CONSENTS_GRID_CODE = 'customer-gdpr';
type FileCellData = {id: string; remoteUrl: string; title: string; contentType: string};

export function GdprConsentsList(props: GDPRConsentsProps) {
  const [deleteConsent] = useDeleteConsentMutation();
  const [withdrawConsent] = useWithdrawConsentMutation();
  const [rejectConsent] = useRejectConsentMutation();
  const [agreeConsent] = useAgreeConsentMutation();

  const {data: esigno} = useGetEsignoServiceQuery();

  const [canSignDocuments] = usePermissions({permissionKeys: ['signDocument']});

  const isSignatureAvailable =
    isFeatureEnabled(featureFlags.DIGITAL_SIGNATURE) && canSignDocuments && isTrue(esigno?.enabled);

  useSubscribeToNotificationsQuery((notification) => {
    if (notification.type === 'file_storage-file_uploaded') {
      refreshDmsDatagrid(GDPR_CONSENTS_GRID_CODE);
    }
  });

  const actionCallback: ActionCallback<typeof PREDEFINE_COLUMNS> = ({
    rowId,
    actionKey,
    getColumnData,
    refreshData,
  }) => {
    const options = {consentId: isArray(rowId) ? rowId[0] : rowId, customerId: props.customerId};
    const file = getColumnData<FileCellData>('fileName')?.value;
    const originalFile = getColumnData<FileCellData>('originalFileName', {isOptional: true})?.value;

    match(actionKey)
      .with('delete', () =>
        openDeleteDialog({
          onConfirm: () =>
            deleteConsent(options)
              .unwrap()
              .then(() => {
                refreshData();
              })
              .catch(handleApiError),
        })
      )
      .with('openOnNewTab', () => {
        if (!file?.remoteUrl) {
          showNotification.error();
          return;
        }

        const previewUrl = getFilePreviewUrl(file?.remoteUrl, file?.contentType);
        openFile(previewUrl);
      })
      .with('download', () => {
        if (!file?.remoteUrl) {
          showNotification.error();
          return;
        }

        downloadHostedFile(file.remoteUrl, file.title);
      })
      .with('sign', () => {
        const fileId = originalFile?.id ?? file?.id;

        if (!fileId) {
          showNotification.error();
          return;
        }

        openDialog(
          <SignDocumentStepper
            fileId={fileId}
            contextId={props.customerId}
            customerId={props.customerId}
            contextTarget="customer"
            refreshData={refreshData}
            data-testid="documentSignature"
            contextSource="gdpr"
          />,
          {
            title: i18n.t('entity.document.actions.signDocument'),
            withAdditionalFooter: true,
            id: DOCUMENT_SIGNATURE_DIALOG_ID,
            size: 'large',
          }
        );
      })
      .with('withdraw', () =>
        withdrawConsent(options)
          .unwrap()
          .then(() => {
            refreshData();
          })
          .catch(handleApiError)
      )
      .with('reject', () =>
        rejectConsent(options)
          .unwrap()
          .then(() => {
            refreshData();
          })
          .catch(handleApiError)
      )
      .with('agree', () =>
        agreeConsent(options)
          .unwrap()
          .then(() => {
            refreshData();
          })
          .catch(handleApiError)
      )
      .otherwise(() => {
        showNotification.error();
        return;
      });
  };

  const handleAddConsent = () =>
    openDialog(
      <AddConsentForm
        businessCaseId={props.businessCaseId ?? null}
        customerId={props.customerId}
      />,
      {
        title: i18n.t('entity.gdpr.labels.addConsent'),
        scrollBehavior: 'outside',
      }
    );

  const queryModifier = (filter: QueryFilterObject) =>
    mergeAll([filter, {customerId: props.customerId}]);

  return (
    <Card
      title={props.title || i18n.t('entity.gdpr.labels.consents')}
      subtitle={props.subtitle}
      actions={buildArray<Action>()
        .add({
          type: 'button',
          variant: 'link',
          isDisabled: isNil(props.customerId),
          onClick: handleAddConsent,
          leftIcon: 'content/add_circle',
          title: i18n.t('entity.gdpr.actions.addNewConsent'),
          'data-testid': testIds.businessCase.customer('gdpr-consent-create-button'),
        })
        .when(isSignatureAvailable, () => ({
          type: 'button',
          variant: 'link',
          leftIcon: 'editor/border_color',
          title: i18n.t('entity.document.actions.signMultipleDocument'),
          onClick: () =>
            openDialog(
              <SignDocumentStepper
                contextId={props.customerId}
                customerId={props.customerId}
                contextTarget="customer"
                refreshData={() => refreshDmsDatagrid(GDPR_CONSENTS_GRID_CODE)}
                data-testid="documentSignature"
                contextSource="gdpr"
              />,
              {
                title: i18n.t('entity.document.actions.signDocument'),
                withAdditionalFooter: true,
                id: DOCUMENT_SIGNATURE_DIALOG_ID,
                size: 'large',
              }
            ),
        }))}
    >
      <DmsDataGrid
        gridCode={GDPR_CONSENTS_GRID_CODE}
        autoHeight
        queryModifier={queryModifier}
        predefinedColumns={PREDEFINE_COLUMNS}
        actionCallback={actionCallback}
      />
    </Card>
  );
}
